import { Box, IconButton, ListItem, Typography } from "@mui/material";

import { AssignmentResponse } from "@/api";
import { AssignmentEditDialog } from "@/common/components/assignments/assignment-edit/AssignmentEditDialog";
import { CompletedCircleIcon } from "@/common/components/assignments/assignment-widget/components/completed-circle-icon/CompletedCircleIcon";
import { CompletionOverlay } from "@/common/components/assignments/assignment-widget/components/completion-overlay/CompletionOverlay";
import { OpenCircleIcon } from "@/common/components/assignments/assignment-widget/components/open-circle-icon/OpenCircleIcon";
import { CaseNoteLink } from "@/common/components/case-note-link/CaseNoteLink";
import { ClickableRow } from "@/common/components/clickable-row/ClickableRow";
import { ClientLink } from "@/common/components/client-link/ClientLink";
import { EllipsisText } from "@/common/components/ellipsis-text/EllipsisText";
import { RowActionButton } from "@/common/components/row-action-button/RowActionButton";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useTranslation } from "@/translations";

import { formatAssignmentDueDate } from "./AssignmentWidget.utils";
import { AssignmentPriorityIndicator } from "./components/priority-icon/AssignmentPriorityIndicator";
import { useAssignment } from "./hooks/useAssignment";

interface Props {
  assignment: AssignmentResponse;
}

export function AssignmentWidget({ assignment }: Props) {
  const { language } = useLanguageContext();
  const {
    completeAssignment,
    completing,
    setCompleting,
    editing,
    setEditing,
    isCompleted,
    reopening,
    setReopening,
    reopenAssignment,
    isTogglingStatus,
  } = useAssignment(assignment);
  const { t } = useTranslation();

  const handleIconClick = () => {
    if (isCompleted) {
      setReopening((prev) => !prev);
    } else {
      setCompleting((prev) => !prev);
    }
  };

  const openEditing = () => !completing && !reopening && setEditing(true);

  return (
    <>
      <ListItem
        disablePadding
        sx={(theme) => ({
          position: "relative",
          overflow: "hidden",
          borderBottom: "1px solid",
          borderColor: theme.palette.fsw["blue-10"],
          minHeight: "69px",
        })}
      >
        {completing && <CompletionOverlay onAnimationEnd={completeAssignment} />}
        {reopening && <CompletionOverlay onAnimationEnd={reopenAssignment} />}

        <ClickableRow
          onClick={openEditing}
          sx={{
            flex: 1,
            py: 1.5,
            position: "relative",
            zIndex: 1,
          }}
        >
          <IconButton
            size="small"
            disabled={isTogglingStatus}
            onClick={(e) => {
              e.stopPropagation();
              handleIconClick();
            }}
            sx={{ ml: 0, mr: 1, flexShrink: 0 }}
            aria-label={isCompleted ? t("assignments.markOpen") : t("assignments.markCompleted")}
          >
            {completing || (isCompleted && !reopening) ? <CompletedCircleIcon /> : <OpenCircleIcon />}
          </IconButton>
          <Box flex={1} minWidth={0}>
            <RowActionButton onClick={openEditing}>
              <EllipsisText
                value={assignment.description}
                variant="body1"
                color={isCompleted ? "text.secondary" : undefined}
              />
            </RowActionButton>
            <Box display="flex" alignItems="center" gap={0.5}>
              <AssignmentPriorityIndicator priority={assignment.priority} />
              <Typography variant="body2" color="text.secondary">
                {formatAssignmentDueDate(assignment, locales[language])}
              </Typography>
              {assignment.assignedClientId && assignment.assignedClientFullName && (
                <ClientLink
                  clientId={assignment.assignedClientId}
                  name={assignment.assignedClientFullName}
                  variant="body2"
                />
              )}
              {assignment.assignedClientId && assignment.assignedCaseNoteId && (
                <>
                  <Typography variant="body2" color="text.secondary">
                    ›
                  </Typography>
                  <CaseNoteLink
                    clientId={assignment.assignedClientId}
                    caseNoteId={assignment.assignedCaseNoteId}
                    label={assignment.assignedCaseNoteLabel ?? t("assignments.creationDialog.caseNote")}
                    variant="body2"
                  />
                </>
              )}
            </Box>
          </Box>
        </ClickableRow>
      </ListItem>

      {editing && <AssignmentEditDialog assignment={assignment} onClose={() => setEditing(false)} />}
    </>
  );
}
